/**
 * Optional Web Audio–based UI cues.
 * Gated by settings.soundEnabled; respects browser autoplay (call resume from user gesture).
 */

let audioCtx: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!audioCtx) {
    audioCtx = new AudioContext()
  }
  return audioCtx
}

export async function resumeAudioContext(): Promise<void> {
  const ctx = getCtx()
  if (ctx?.state === 'suspended') {
    await ctx.resume()
  }
}

function playShortTone(
  soundEnabled: boolean,
  startHz: number,
  endHz: number,
  duration: number,
  volume: number,
): void {
  if (!soundEnabled) return
  const ctx = getCtx()
  if (!ctx) return
  void resumeAudioContext()
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  gain.gain.value = volume
  osc.type = 'triangle'
  const t0 = ctx.currentTime
  osc.frequency.setValueAtTime(startHz, t0)
  osc.frequency.exponentialRampToValueAtTime(Math.max(40, endHz), t0 + duration)
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.start(t0)
  osc.stop(t0 + duration)
}

export function playExpeditionSendCue(soundEnabled: boolean): void {
  playShortTone(soundEnabled, 380, 200, 0.16, 0.07)
}

export function playExpeditionReturnCue(soundEnabled: boolean): void {
  playShortTone(soundEnabled, 180, 360, 0.2, 0.08)
}

export function playRecipeCraftCue(soundEnabled: boolean): void {
  playShortTone(soundEnabled, 520, 660, 0.1, 0.05)
}

/** Bicycle bell — two quick bright dings */
export function playBikeBellCue(soundEnabled: boolean): void {
  if (!soundEnabled) return
  const ctx = getCtx()
  if (!ctx) return
  void resumeAudioContext()
  const t0 = ctx.currentTime
  for (let i = 0; i < 2; i++) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const start = t0 + i * 0.18
    osc.type = 'sine'
    osc.frequency.setValueAtTime(1800, start)
    osc.frequency.exponentialRampToValueAtTime(1400, start + 0.12)
    gain.gain.setValueAtTime(0.12, start)
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.12)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(start)
    osc.stop(start + 0.13)
  }
}

/** Car engine — short low rumble with a rev */
export function playCarEngineCue(soundEnabled: boolean): void {
  if (!soundEnabled) return
  const ctx = getCtx()
  if (!ctx) return
  void resumeAudioContext()
  const t0 = ctx.currentTime
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sawtooth'
  osc.frequency.setValueAtTime(60, t0)
  osc.frequency.exponentialRampToValueAtTime(140, t0 + 0.15)
  osc.frequency.exponentialRampToValueAtTime(80, t0 + 0.35)
  gain.gain.setValueAtTime(0.0, t0)
  gain.gain.linearRampToValueAtTime(0.1, t0 + 0.05)
  gain.gain.setValueAtTime(0.1, t0 + 0.25)
  gain.gain.linearRampToValueAtTime(0.0, t0 + 0.38)
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.start(t0)
  osc.stop(t0 + 0.4)
}

export function playVehicleDepartureCue(
  soundEnabled: boolean,
  audioKind: 'bike' | 'car',
): void {
  if (audioKind === 'bike') {
    playBikeBellCue(soundEnabled)
  } else {
    playCarEngineCue(soundEnabled)
  }
}
